/**
 * Converts a YouTube URL to its embed format
 * Supports various YouTube URL formats:
 * - youtu.be/VIDEO_ID
 * - youtube.com/watch?v=VIDEO_ID
 * - youtube.com/shorts/VIDEO_ID
 * - youtube.com/embed/VIDEO_ID
 * - youtube.com/v/VIDEO_ID
 *
 * @param url - The YouTube URL to convert
 * @returns The embed URL or the original URL if parsing fails
 */
export const getYouTubeEmbedUrl = (url: string): string => {
  try {
    // Trim — editors sometimes paste URLs with leading/trailing whitespace which
    // otherwise breaks the protocol check and pattern match below.
    url = (url ?? '').trim()
    // Basic URL validation
    if (!url.match(/^https?:\/\//)) {
      url = 'https://' + url
    }

    // Extract video ID using regex to handle all formats
    const patterns = [
      /(?:youtu\.be\/|youtube\.com\/shorts\/)([^?&#]+)/, // youtu.be and shorts
      /youtube\.com\/.*[?&]v=([^?&#]+)/, // standard with params
      /youtube\.com\/(?:embed|v)\/([^?&#]+)/, // embed and v paths
    ]

    let videoId: string | null = null
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match?.[1]) {
        videoId = match[1]
        break
      }
    }

    if (!videoId) {
      return url // Return original if no valid ID found
    }

    // Build embed URL, preserving only time parameter if present
    const originalUrl = new URL(url)
    const startTime = originalUrl.searchParams.get('t') || originalUrl.searchParams.get('start')
    let embedUrl = `https://www.youtube.com/embed/${videoId}`
    if (startTime) {
      embedUrl += `?start=${startTime}`
    }

    return embedUrl
  } catch {
    return url // Return original URL if parsing fails
  }
}

/**
 * Checks if a URL is a YouTube URL
 */
export const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be')
}
